import { useCallback, useEffect, useRef, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  startDemo,
  advanceDemoStep,
  setDemoStep,
  setWinningOption,
  setSecondWinningOption,
  endDemo,
} from "@store/demoSlice";
import { DEMO_STEPS, DEMO_STEP_ORDER } from "@constants/demoFlow";
import {
  useStartDemoSimulationMutation,
  useTriggerDemoEmojiWaveMutation,
} from "@store/api/agenda";
import { trackDemoEvent } from "@utils/demoAnalytics";

const ENGAGEMENT_SIGNAL_DURATION_MS = 6000;

/**
 * Core orchestration hook for the cinematic demo flow.
 * Replaces useDemoTour.js.
 *
 * Auto-advance triggers:
 * - SHARE_LINK -> COPY_LINK: when share modal opens
 * - COPY_LINK -> SHARE_LINK (regression): when share modal closes without copying
 * - COPY_LINK -> START_MEETING: via advance() callback from onCopy
 * - START_MEETING -> CROWD_ARRIVING: when agenda.live becomes true
 * - CROWD_ARRIVING -> ENGAGEMENT_SIGNAL: when participantCount >= 8 OR after 13s timeout
 * - ENGAGEMENT_SIGNAL -> PLAY_ACTIVITY: after the chart has been explained
 * - PLAY_ACTIVITY -> ACTIVITY_RUNNING: when first activity goes live
 * - ACTIVITY_RUNNING -> VIEW_ATTENDEE_DETAIL: when first activity has endTime
 * - VIEW_ATTENDEE_DETAIL -> RESULTS_MODAL: when the attendee detail opens (then auto-closes); fallback 10s
 * - PLAY_SECOND_ACTIVITY -> SECOND_ACTIVITY_RUNNING: when second activity goes live
 * - SECOND_ACTIVITY_RUNNING -> VIEW_SECOND_ATTENDEE_DETAIL: when second activity has endTime
 * - VIEW_SECOND_ATTENDEE_DETAIL -> SECOND_RESULTS_MODAL: same pattern as the first attendee detail step
 * - GUIDE_STOP -> REPORT_CTA: when agenda.endTime is set
 */
const useDemoFlow = ({
  agenda,
  activities,
  isShareModalOpen,
  participantCount,
}) => {
  const dispatch = useDispatch();
  const timersRef = useRef([]);
  const hasAdvancedRef = useRef({});

  // Redux demo state
  const isActive = useSelector((state) => state.demo.isActive);
  const currentStep = useSelector((state) => state.demo.currentStep);
  const winningOption = useSelector((state) => state.demo.winningOption);
  const winningVoteCount = useSelector((state) => state.demo.winningVoteCount);
  const totalVotes = useSelector((state) => state.demo.totalVotes);
  const secondWinningOption = useSelector(
    (state) => state.demo.secondWinningOption
  );
  const secondWinningVoteCount = useSelector(
    (state) => state.demo.secondWinningVoteCount
  );
  const secondTotalVotes = useSelector((state) => state.demo.secondTotalVotes);
  const demoStartTime = useSelector((state) => state.demo.demoStartTime);

  const [startDemoSimulation] = useStartDemoSimulationMutation();
  const [triggerEmojiWave] = useTriggerDemoEmojiWaveMutation();

  // Memoize the first activity for the results modal
  const firstActivity = useMemo(() => {
    if (!activities) return null;
    return activities.find((a) => a.order === 0) || activities[0];
  }, [activities]);

  // Memoize the second activity (order === 1 or second in array)
  const secondActivity = useMemo(() => {
    if (!activities || activities.length < 2) return null;
    return activities.find((a) => a.order === 1) || activities[1];
  }, [activities]);

  // Memoize the third activity (order === 2 or third in array)
  const thirdActivity = useMemo(() => {
    if (!activities || activities.length < 3) return null;
    return activities.find((a) => a.order === 2) || activities[2];
  }, [activities]);

  // Memoize the fourth activity (order === 3 or fourth in array)
  const fourthActivity = useMemo(() => {
    if (!activities || activities.length < 4) return null;
    return activities.find((a) => a.order === 3) || activities[3];
  }, [activities]);

  // Capitalize first letter of activity type for display
  const firstActivityTypeLabel = useMemo(() => {
    if (!firstActivity?.type) return "Activity";
    return (
      firstActivity.type.charAt(0).toUpperCase() + firstActivity.type.slice(1)
    );
  }, [firstActivity?.type]);

  const secondActivityTypeLabel = useMemo(() => {
    if (!secondActivity?.type) return "Activity";
    return (
      secondActivity.type.charAt(0).toUpperCase() + secondActivity.type.slice(1)
    );
  }, [secondActivity?.type]);

  // Use displayName for share-type activities (link, announcement) since their type defaults to "share"
  const thirdActivityTypeLabel = useMemo(() => {
    if (!thirdActivity?.displayName) return "Activity";
    return thirdActivity.displayName;
  }, [thirdActivity?.displayName]);

  const fourthActivityTypeLabel = useMemo(() => {
    if (!fourthActivity?.displayName) return "Activity";
    return fourthActivity.displayName;
  }, [fourthActivity?.displayName]);

  // Get current step config with dynamic overrides
  const stepConfig = useMemo(() => {
    if (!currentStep || !DEMO_STEPS[currentStep]) return null;
    const config = { ...DEMO_STEPS[currentStep] };

    // Dynamic body for RESULTS_MODAL
    if (currentStep === "RESULTS_MODAL" && winningOption) {
      config.body = `Hmm. Restructure operations was highest. Perhaps the team is misaligned on roles?`;
    }

    // Dynamic heading, body, and targetSelector for PLAY_ACTIVITY
    if (currentStep === "PLAY_ACTIVITY" && firstActivity) {
      config.heading = `Let's play a ${firstActivityTypeLabel}!`;
      config.body = `Click 'Play' to start the ${firstActivityTypeLabel} and watch the responses come in live.`;
      config.targetSelector = `[data-testid="activity-live-button-${firstActivity._id}"]`;
    }

    // Dynamic heading, body, and targetSelector for PLAY_SECOND_ACTIVITY
    if (currentStep === "PLAY_SECOND_ACTIVITY" && secondActivity) {
      config.heading = `Let's test to see if people were paying attention!`;
      config.body = `Click 'Play' to start the ${secondActivityTypeLabel} and see how your audience does.`;
      config.targetSelector = `[data-testid="activity-live-button-${secondActivity._id}"]`;
    }

    // Dynamic targetSelector for VIEW_ATTENDEE_DETAIL (first activity)
    if (currentStep === "VIEW_ATTENDEE_DETAIL" && firstActivity) {
      config.targetSelector = `[data-testid="attendee-detail-button-${firstActivity._id}"]`;
    }

    // Dynamic body for SECOND_RESULTS_MODAL
    if (currentStep === "SECOND_RESULTS_MODAL" && secondWinningOption) {
      config.body = `Looks like most got it correct. Nice job! NPS is the best metric.`;
    }

    // Dynamic targetSelector for VIEW_SECOND_ATTENDEE_DETAIL (second activity)
    if (currentStep === "VIEW_SECOND_ATTENDEE_DETAIL" && secondActivity) {
      config.targetSelector = `[data-testid="attendee-detail-button-${secondActivity._id}"]`;
    }

    // Dynamic heading and targetSelector for PLAY_THIRD_ACTIVITY
    if (currentStep === "PLAY_THIRD_ACTIVITY" && thirdActivity) {
      config.heading = `Now let's share a ${thirdActivityTypeLabel}!`;
      config.targetSelector = `[data-testid="activity-live-button-${thirdActivity._id}"]`;
    }

    // Dynamic heading and targetSelector for PLAY_FOURTH_ACTIVITY
    if (currentStep === "PLAY_FOURTH_ACTIVITY" && fourthActivity) {
      config.heading = `Now let's share an ${fourthActivityTypeLabel}!`;
      config.targetSelector = `[data-testid="activity-live-button-${fourthActivity._id}"]`;
    }

    return config;
  }, [
    currentStep,
    winningOption,
    firstActivity,
    firstActivityTypeLabel,
    secondActivity,
    secondActivityTypeLabel,
    secondWinningOption,
    thirdActivity,
    thirdActivityTypeLabel,
    fourthActivity,
    fourthActivityTypeLabel,
  ]);

  // Clear all pending timers
  const clearTimers = useCallback(() => {
    timersRef.current.forEach((t) => clearTimeout(t));
    timersRef.current = [];
  }, []);

  // Initialize demo
  const initDemo = useCallback(
    (agendaId) => {
      hasAdvancedRef.current = {};
      dispatch(startDemo({ agendaId }));
      trackDemoEvent("demo_started", { agendaId });
    },
    [dispatch]
  );

  // Advance to next step
  const advance = useCallback(() => {
    if (!isActive || !currentStep) return;
    const currentIndex = DEMO_STEP_ORDER.indexOf(currentStep);
    const nextStep = DEMO_STEP_ORDER[currentIndex + 1];

    trackDemoEvent("demo_step_reached", {
      step: nextStep,
      stepIndex: currentIndex + 1,
      timeElapsedMs: demoStartTime ? Date.now() - demoStartTime : 0,
    });

    dispatch(advanceDemoStep());
  }, [isActive, currentStep, demoStartTime, dispatch]);

  // Stop demo
  const stopDemo = useCallback(() => {
    clearTimers();
    trackDemoEvent("demo_abandoned", {
      lastStep: currentStep,
      timeElapsedMs: demoStartTime ? Date.now() - demoStartTime : 0,
    });
    dispatch(endDemo());
    hasAdvancedRef.current = {};
  }, [clearTimers, currentStep, demoStartTime, dispatch]);

  // === AUTO-ADVANCE EFFECTS ===

  // SHARE_LINK -> COPY_LINK: when share modal opens
  useEffect(() => {
    if (!isActive || currentStep !== "SHARE_LINK") return;
    if (isShareModalOpen && !hasAdvancedRef.current["SHARE_LINK"]) {
      hasAdvancedRef.current["SHARE_LINK"] = true;
      setTimeout(() => advance(), 300);
    }
  }, [isActive, currentStep, isShareModalOpen, advance]);

  // COPY_LINK regression: if user closes modal without copying, go back to SHARE_LINK
  useEffect(() => {
    if (!isActive || currentStep !== "COPY_LINK") return;
    if (!isShareModalOpen) {
      // Modal was closed without copying — regress to SHARE_LINK
      hasAdvancedRef.current["SHARE_LINK"] = false;
      dispatch(setDemoStep("SHARE_LINK"));
    }
  }, [isActive, currentStep, isShareModalOpen, dispatch]);

  // START_MEETING -> CROWD_ARRIVING: when agenda.live becomes true
  // Also trigger demo simulation now that the socket is connected
  useEffect(() => {
    if (!isActive || currentStep !== "START_MEETING") return;
    if (agenda?.live && !hasAdvancedRef.current["START_MEETING"]) {
      hasAdvancedRef.current["START_MEETING"] = true;

      // Start simulation after a short delay to ensure socket has connected
      if (agenda.isDemo && agenda.meetingUuid) {
        setTimeout(() => {
          startDemoSimulation({
            agendaId: agenda._id,
            meetingInstanceId: agenda.meetingUuid,
          });
        }, 1000);
      }

      setTimeout(() => advance(), 300);
    }
  }, [
    isActive,
    currentStep,
    agenda?.live,
    agenda?.isDemo,
    agenda?.meetingUuid,
    agenda?._id,
    advance,
    startDemoSimulation,
  ]);

  // CROWD_ARRIVING -> PLAY_ACTIVITY: when participantCount >= 8 OR after 13s timeout
  useEffect(() => {
    if (!isActive || currentStep !== "CROWD_ARRIVING") return;
    if (hasAdvancedRef.current["CROWD_ARRIVING"]) return;

    // Advance on participant threshold
    if (participantCount >= 8) {
      hasAdvancedRef.current["CROWD_ARRIVING"] = true;
      setTimeout(() => advance(), 500);
      return;
    }

    // Fallback: advance after 13s timeout
    const timerId = setTimeout(() => {
      if (!hasAdvancedRef.current["CROWD_ARRIVING"]) {
        hasAdvancedRef.current["CROWD_ARRIVING"] = true;
        advance();
      }
    }, 13000);

    timersRef.current.push(timerId);
    return () => clearTimeout(timerId);
  }, [isActive, currentStep, participantCount, advance]);

  // ENGAGEMENT_SIGNAL -> PLAY_ACTIVITY: after the chart has been explained
  useEffect(() => {
    if (!isActive || currentStep !== "ENGAGEMENT_SIGNAL") return;
    if (hasAdvancedRef.current["ENGAGEMENT_SIGNAL"]) return;

    const timerId = setTimeout(() => {
      if (!hasAdvancedRef.current["ENGAGEMENT_SIGNAL"]) {
        hasAdvancedRef.current["ENGAGEMENT_SIGNAL"] = true;
        advance();
      }
    }, ENGAGEMENT_SIGNAL_DURATION_MS);

    timersRef.current.push(timerId);
    return () => clearTimeout(timerId);
  }, [isActive, currentStep, advance]);

  // PLAY_ACTIVITY -> ACTIVITY_RUNNING: when first activity goes live
  useEffect(() => {
    if (!isActive || currentStep !== "PLAY_ACTIVITY" || !activities) return;
    if (hasAdvancedRef.current["PLAY_ACTIVITY"]) return;

    // Use startTime (which persists after auto-stop) so we don't miss a fast
    // activity that already started/ended by the time this step is active.
    const playedActivity = activities.find((a) => a.live || a.startTime);
    if (playedActivity) {
      hasAdvancedRef.current["PLAY_ACTIVITY"] = true;
      trackDemoEvent("demo_activity_played", {
        activityId: playedActivity._id,
        activityType: playedActivity.type,
      });
      setTimeout(() => advance(), 500);
    }
  }, [isActive, currentStep, activities, advance]);

  // ACTIVITY_RUNNING -> VIEW_ATTENDEE_DETAIL: when first activity has endTime
  useEffect(() => {
    if (!isActive || currentStep !== "ACTIVITY_RUNNING" || !activities) return;
    if (hasAdvancedRef.current["ACTIVITY_RUNNING"]) return;

    const first = activities.find((a) => a.order === 0 || a.startTime);
    if (first?.endTime) {
      hasAdvancedRef.current["ACTIVITY_RUNNING"] = true;
      setTimeout(() => advance(), 500);
    }
  }, [isActive, currentStep, activities, advance]);

  // PLAY_SECOND_ACTIVITY -> SECOND_ACTIVITY_RUNNING: when second activity goes live
  useEffect(() => {
    if (!isActive || currentStep !== "PLAY_SECOND_ACTIVITY" || !secondActivity)
      return;
    if (hasAdvancedRef.current["PLAY_SECOND_ACTIVITY"]) return;

    // startTime persists after auto-stop, so a fast activity that already
    // started/ended is still detected here.
    if (secondActivity.live || secondActivity.startTime) {
      hasAdvancedRef.current["PLAY_SECOND_ACTIVITY"] = true;
      trackDemoEvent("demo_activity_played", {
        activityId: secondActivity._id,
        activityType: secondActivity.type,
      });
      setTimeout(() => advance(), 500);
    }
  }, [isActive, currentStep, secondActivity, advance]);

  // SECOND_ACTIVITY_RUNNING -> VIEW_SECOND_ATTENDEE_DETAIL: when second activity has endTime
  useEffect(() => {
    if (
      !isActive ||
      currentStep !== "SECOND_ACTIVITY_RUNNING" ||
      !secondActivity
    )
      return;
    if (hasAdvancedRef.current["SECOND_ACTIVITY_RUNNING"]) return;

    if (secondActivity.endTime) {
      hasAdvancedRef.current["SECOND_ACTIVITY_RUNNING"] = true;
      setTimeout(() => advance(), 500);
    }
  }, [isActive, currentStep, secondActivity, advance]);

  // VIEW_ATTENDEE_DETAIL -> READ_ATTENDEE_DETAIL on click (popover unmounts).
  // Fallback skips straight to RESULTS_MODAL — no point pausing to read what
  // the user didn't engage with.
  useEffect(() => {
    if (!isActive || currentStep !== "VIEW_ATTENDEE_DETAIL" || !firstActivity)
      return;
    if (hasAdvancedRef.current["VIEW_ATTENDEE_DETAIL"]) return;

    const selector = `[data-testid="attendee-detail-view-${firstActivity._id}"]`;
    const intervalId = setInterval(() => {
      if (document.querySelector(selector)) {
        hasAdvancedRef.current["VIEW_ATTENDEE_DETAIL"] = true;
        clearInterval(intervalId);
        advance();
      }
    }, 200);

    const fallbackId = setTimeout(() => {
      if (!hasAdvancedRef.current["VIEW_ATTENDEE_DETAIL"]) {
        hasAdvancedRef.current["VIEW_ATTENDEE_DETAIL"] = true;
        clearInterval(intervalId);
        dispatch(setDemoStep("RESULTS_MODAL"));
      }
    }, 10000);

    timersRef.current.push(fallbackId);
    return () => {
      clearInterval(intervalId);
      clearTimeout(fallbackId);
    };
  }, [isActive, currentStep, firstActivity, advance, dispatch]);

  // READ_ATTENDEE_DETAIL -> RESULTS_MODAL after a short pause.
  useEffect(() => {
    if (!isActive || currentStep !== "READ_ATTENDEE_DETAIL") return;
    if (hasAdvancedRef.current["READ_ATTENDEE_DETAIL"]) return;

    const timerId = setTimeout(() => {
      if (hasAdvancedRef.current["READ_ATTENDEE_DETAIL"]) return;
      hasAdvancedRef.current["READ_ATTENDEE_DETAIL"] = true;
      document.querySelector('[data-testid="back-to-summary"]')?.click();
      advance();
    }, 4000);

    timersRef.current.push(timerId);
    return () => clearTimeout(timerId);
  }, [isActive, currentStep, advance]);

  // VIEW_SECOND_ATTENDEE_DETAIL -> READ_SECOND_ATTENDEE_DETAIL (same as first).
  useEffect(() => {
    if (
      !isActive ||
      currentStep !== "VIEW_SECOND_ATTENDEE_DETAIL" ||
      !secondActivity
    )
      return;
    if (hasAdvancedRef.current["VIEW_SECOND_ATTENDEE_DETAIL"]) return;

    const selector = `[data-testid="attendee-detail-view-${secondActivity._id}"]`;
    const intervalId = setInterval(() => {
      if (document.querySelector(selector)) {
        hasAdvancedRef.current["VIEW_SECOND_ATTENDEE_DETAIL"] = true;
        clearInterval(intervalId);
        advance();
      }
    }, 200);

    const fallbackId = setTimeout(() => {
      if (!hasAdvancedRef.current["VIEW_SECOND_ATTENDEE_DETAIL"]) {
        hasAdvancedRef.current["VIEW_SECOND_ATTENDEE_DETAIL"] = true;
        clearInterval(intervalId);
        dispatch(setDemoStep("SECOND_RESULTS_MODAL"));
      }
    }, 10000);

    timersRef.current.push(fallbackId);
    return () => {
      clearInterval(intervalId);
      clearTimeout(fallbackId);
    };
  }, [isActive, currentStep, secondActivity, advance, dispatch]);

  // READ_SECOND_ATTENDEE_DETAIL -> SECOND_RESULTS_MODAL (same as first).
  useEffect(() => {
    if (!isActive || currentStep !== "READ_SECOND_ATTENDEE_DETAIL") return;
    if (hasAdvancedRef.current["READ_SECOND_ATTENDEE_DETAIL"]) return;

    const timerId = setTimeout(() => {
      if (hasAdvancedRef.current["READ_SECOND_ATTENDEE_DETAIL"]) return;
      hasAdvancedRef.current["READ_SECOND_ATTENDEE_DETAIL"] = true;
      document.querySelector('[data-testid="back-to-summary"]')?.click();
      advance();
    }, 4000);

    timersRef.current.push(timerId);
    return () => clearTimeout(timerId);
  }, [isActive, currentStep, advance]);

  // PLAY_THIRD_ACTIVITY -> THIRD_ACTIVITY_RUNNING: when third activity goes live
  useEffect(() => {
    if (!isActive || currentStep !== "PLAY_THIRD_ACTIVITY" || !thirdActivity)
      return;
    if (hasAdvancedRef.current["PLAY_THIRD_ACTIVITY"]) return;

    if (thirdActivity.live || thirdActivity.startTime) {
      hasAdvancedRef.current["PLAY_THIRD_ACTIVITY"] = true;
      trackDemoEvent("demo_activity_played", {
        activityId: thirdActivity._id,
        activityType: thirdActivity.type,
      });
      setTimeout(() => advance(), 500);
    }
  }, [isActive, currentStep, thirdActivity, advance]);

  // THIRD_ACTIVITY_RUNNING -> PLAY_FOURTH_ACTIVITY: timeout after 5s (shares don't auto-stop)
  useEffect(() => {
    if (!isActive || currentStep !== "THIRD_ACTIVITY_RUNNING") return;
    if (hasAdvancedRef.current["THIRD_ACTIVITY_RUNNING"]) return;

    const timerId = setTimeout(() => {
      if (!hasAdvancedRef.current["THIRD_ACTIVITY_RUNNING"]) {
        hasAdvancedRef.current["THIRD_ACTIVITY_RUNNING"] = true;
        advance();
      }
    }, 5000);

    timersRef.current.push(timerId);
    return () => clearTimeout(timerId);
  }, [isActive, currentStep, advance]);

  // PLAY_FOURTH_ACTIVITY -> FOURTH_ACTIVITY_RUNNING: when fourth activity goes live
  useEffect(() => {
    if (!isActive || currentStep !== "PLAY_FOURTH_ACTIVITY" || !fourthActivity)
      return;
    if (hasAdvancedRef.current["PLAY_FOURTH_ACTIVITY"]) return;

    if (fourthActivity.live || fourthActivity.startTime) {
      hasAdvancedRef.current["PLAY_FOURTH_ACTIVITY"] = true;
      trackDemoEvent("demo_activity_played", {
        activityId: fourthActivity._id,
        activityType: fourthActivity.type,
      });
      setTimeout(() => advance(), 500);
    }
  }, [isActive, currentStep, fourthActivity, advance]);

  // FOURTH_ACTIVITY_RUNNING -> GUIDE_STOP: timeout after 5s (shares don't auto-stop)
  useEffect(() => {
    if (!isActive || currentStep !== "FOURTH_ACTIVITY_RUNNING") return;
    if (hasAdvancedRef.current["FOURTH_ACTIVITY_RUNNING"]) return;

    const timerId = setTimeout(() => {
      if (!hasAdvancedRef.current["FOURTH_ACTIVITY_RUNNING"]) {
        hasAdvancedRef.current["FOURTH_ACTIVITY_RUNNING"] = true;
        advance();
      }
    }, 5000);

    timersRef.current.push(timerId);
    return () => clearTimeout(timerId);
  }, [isActive, currentStep, advance]);

  // Auto-expand the Detail accordion while an activity is running. Polled
  // because refetches can briefly re-collapse it after a single click.
  useEffect(() => {
    if (!isActive) return;
    if (
      currentStep !== "ACTIVITY_RUNNING" &&
      currentStep !== "SECOND_ACTIVITY_RUNNING" &&
      currentStep !== "THIRD_ACTIVITY_RUNNING" &&
      currentStep !== "FOURTH_ACTIVITY_RUNNING" &&
      currentStep !== "VIEW_ATTENDEE_DETAIL" &&
      currentStep !== "READ_ATTENDEE_DETAIL" &&
      currentStep !== "VIEW_SECOND_ATTENDEE_DETAIL" &&
      currentStep !== "READ_SECOND_ATTENDEE_DETAIL"
    )
      return;

    const activityMap = {
      ACTIVITY_RUNNING: firstActivity,
      SECOND_ACTIVITY_RUNNING: secondActivity,
      THIRD_ACTIVITY_RUNNING: thirdActivity,
      FOURTH_ACTIVITY_RUNNING: fourthActivity,
      VIEW_ATTENDEE_DETAIL: firstActivity,
      READ_ATTENDEE_DETAIL: firstActivity,
      VIEW_SECOND_ATTENDEE_DETAIL: secondActivity,
      READ_SECOND_ATTENDEE_DETAIL: secondActivity,
    };
    const targetActivity = activityMap[currentStep];
    if (!targetActivity?._id) return;

    const accordionSelector = `[data-testid="activity-details-accordion-button-${targetActivity._id}"]`;
    let attempts = 0;
    const intervalId = setInterval(() => {
      attempts += 1;
      const accordionBtn = document.querySelector(accordionSelector);
      if (accordionBtn) {
        if (accordionBtn.getAttribute("aria-expanded") !== "true") {
          accordionBtn.click();
        }
      }
      if (attempts >= 50) clearInterval(intervalId); // ~10s budget
    }, 200);

    return () => clearInterval(intervalId);
  }, [
    isActive,
    currentStep,
    firstActivity,
    secondActivity,
    thirdActivity,
    fourthActivity,
  ]);

  // Calculate winning option when entering RESULTS_MODAL or after
  useEffect(() => {
    if (!isActive || !activities) return;
    const stepIndex = DEMO_STEP_ORDER.indexOf(currentStep);
    if (stepIndex < DEMO_STEP_ORDER.indexOf("RESULTS_MODAL")) return;

    const first = activities.find((a) => a.order === 0 || a.startTime);
    if (!first?.response?.length || !first?.options?.length) return;

    // Count votes per option
    const optionVotes = {};
    first.options.forEach((opt) => {
      optionVotes[opt._id] = { count: 0, text: opt.option };
    });

    first.response.forEach((r) => {
      (r.responseIds || []).forEach((rid) => {
        const ridStr = rid.toString ? rid.toString() : rid;
        if (optionVotes[ridStr]) {
          optionVotes[ridStr].count++;
        }
      });
    });

    // Find the winner
    let maxVotes = 0;
    let winner = null;
    Object.values(optionVotes).forEach((v) => {
      if (v.count > maxVotes) {
        maxVotes = v.count;
        winner = v.text;
      }
    });

    if (winner) {
      dispatch(
        setWinningOption({
          option: winner,
          voteCount: maxVotes,
          totalVotes: first.response.length,
        })
      );
    }
  }, [isActive, currentStep, activities, dispatch]);

  // Calculate winning option for second activity when entering SECOND_RESULTS_MODAL or after
  useEffect(() => {
    if (!isActive || !secondActivity) return;
    const stepIndex = DEMO_STEP_ORDER.indexOf(currentStep);
    if (stepIndex < DEMO_STEP_ORDER.indexOf("SECOND_RESULTS_MODAL")) return;

    if (!secondActivity.response?.length || !secondActivity.options?.length)
      return;

    // Count votes per option
    const optionVotes = {};
    secondActivity.options.forEach((opt) => {
      optionVotes[opt._id] = { count: 0, text: opt.option };
    });

    secondActivity.response.forEach((r) => {
      (r.responseIds || []).forEach((rid) => {
        const ridStr = rid.toString ? rid.toString() : rid;
        if (optionVotes[ridStr]) {
          optionVotes[ridStr].count++;
        }
      });
    });

    // Find the winner
    let maxVotes = 0;
    let winner = null;
    Object.values(optionVotes).forEach((v) => {
      if (v.count > maxVotes) {
        maxVotes = v.count;
        winner = v.text;
      }
    });

    if (winner) {
      dispatch(
        setSecondWinningOption({
          option: winner,
          voteCount: maxVotes,
          totalVotes: secondActivity.response.length,
        })
      );
    }
  }, [isActive, currentStep, secondActivity, dispatch]);

  // GUIDE_STOP -> report hand-off is owned by AgendaDetailView (the wrapper),
  // NOT here: the moment agenda.endTime is set the agenda becomes "past" and the
  // wrapper swaps NewAgendaView (which hosts this hook) for PastAgendaView,
  // unmounting this hook. Driving the navigate + REPORT_CTA advance from a
  // component that unmounts at exactly that instant strands the guided flow, so
  // that logic now lives in AgendaDetailView, which stays mounted across the swap.

  // Cleanup on unmount
  useEffect(() => {
    return () => clearTimers();
  }, [clearTimers]);

  return {
    isActive,
    currentStep,
    stepConfig,
    winningOption,
    winningVoteCount,
    totalVotes,
    firstActivity,
    secondActivity,
    thirdActivity,
    fourthActivity,
    initDemo,
    advance,
    stopDemo,
    triggerEmojiWave,
  };
};

export default useDemoFlow;
